// TransportV2Context - Zentraler State-Container fuer die V2-Page.
// Der Reducer bildet exakt das state-Shape aus dem Mock ab, ergaenzt um
// Live-Daten (instances, packages, precheckByBucket, globalRefs, orphans,
// artifactConfigs, pipelineRun, releaseCandidate).
//
// Wichtige Konventionen:
//   - state.sel[sourceId][werkKey] = true   (Werk-Auswahl, Key = Source-Id)
//   - state.cfg[werkKey][sourceId][paramKey] = normalizedConfigEntry
//   - state.precheckByBucket[bucketKey] = { comparison, orphans, references, error }
//     bucketKey = `${targetInstanceId}__${werkKennung||"std"}`
//   - state.pipeline: idle | snapshotting | dispatching | running | done | pending | failed | released
//   - suffixedId wird NIE rekonstruiert, immer aus precheck durchgereicht.

import React, { createContext, useContext, useMemo, useReducer, useLayoutEffect } from "react";
import { toggleConfigSuggestions } from "./configSuggestions";
import { normalizeEntryAdapterValues } from "../../../utils/transport-config-helpers";
import { DEFAULT_PLANT_NAMING, validPlantNaming } from "../utils/plantNaming";
import { needsSameVersionPermission, wasSameVersionSelected, isArtifactDraft, needsSameReferenceVersionPermission, sameReferenceVersionPair } from "../utils/sameVersion";
import { isPackageTransport, packageDesignObjects } from "../utils/packageTransport";
import { canonicalTopicSpace } from "../../../hooks/useSpaceTopics";
import { allTargetColumns, applyStageTargets, forwardTargets, normalizeSelection, targetColumns } from "../utils/stageTargetSpace";
import { plantTargetPreset } from "../utils/plantTargetPreset";

export function canTransportSameVersion(user) {
  return user?.role === "superadmin" || (user?.role === "admin" && Array.isArray(user.adminPermissions) && user.adminPermissions.includes("transportSameVersion"));
}

export function canTransportSameVersionReferences(user) {
  return user?.role === "superadmin" || (user?.role === "admin" && Array.isArray(user.adminPermissions) && user.adminPermissions.includes("transportSameVersionReferences"));
}

const initialState = {
  // Mehrzielmodus (Vertrag V.2): Ziele je Stufe aus der Pipeline-Übersicht und die Wizard-Auswahl je Stufe.
  multiTarget: false,
  stageTargets: null,
  stageTargetSel: {},
  // Erst nach einem erfolgreichen Lesen von GET .../stage-branches/config (Vertrag V.12) gelten die Ziele als bekannt.
  stageTargetsRead: false,
  canTransportSameVersion: false,
  canTransportSameVersionReferences: false,
  plantNaming: DEFAULT_PLANT_NAMING,
  plantChangeConfirmations: [],
  selectedPackages: [],
  packageDrafts: {},
  packageEpoch: 0,
  precheckGeneration: 0,
  precheckRequest: 0,
  precheckToken: null,
  precheckPhases: [],
  precheckError: null,
  precheckRefsWarning: false,
  precheckReleasedId: null,
  packagePrepared: false,
  configReviewed: false,
  pdPrefillKey: null,
  cfgErrors: {},
  batch: null,
  batchRequest: null,
  step: 0,
  spaceKey: null,
  spaceId: null,
  topicId: null,
  topicSpaceId: null,
  topicSelection: null,
  topicSourceInstanceId: null,
  space: null,
  sourceInstanceId: null,
  targetInstanceIds: [],
  werkOn: {},
  pkgId: null,
  pkgName: null,
  pkgVersion: null,
  pkgMod: null,
  precheckByBucket: {},
  precheckState: "idle",
  globalRefs: [],
  refSel: {},
  artifactDeselection: {},
  globalRefDeselection: {},
  orphanSel: {},
  expanded: {},
  sel: {},
  deploy: {},
  manualEdgeDeploy: {},
  deployStages: {},
  manualEdgeStages: {},
  // Pakettransport (v27): Paketweites CI Deployment (Standard aus), je Stufe steuerbar.
  packageDeploy: false,
  packageDeployStages: {},
  // Pakettransport (v27): Edge je deploybarem Designobjekt (Standard aus, alle Stufen gleich).
  designEdgeDeploy: {},
  // Version der Erweiterung im Space-Repository (null = unbekannt). Steuert die Pakettransport-Schalter.
  extensionVersion: null,
  refDeploy: {},
  cfg: {},
  cfgSuggestionUndo: null,
  // Parameter values maintained in Git, preloaded per kennung and source artifact:
  //   { "<kennung>" | "": { "<sourceArtifactId>": { "<paramKey>": { DataType, Description,
  //                                                                 values: { __base__, <stage> } } } } }
  // Git is the truth for the parameters. Without this preload the wizard showed only the tenants'
  // current values and the snapshot discarded everything it did not know - a QAS transport thus
  // deleted the maintained PROD values.
  gitCfg: {},
  gitCfgScope: null,
  // Only da/leer permit the configuration view to continue; failed reads remain blocked.
  // "leer" = no space or no package, so nothing to preload.
  gitCfgState: "idle",
  configReadGeneration: 0,
  gitCfgUnvollstaendig: false,
  cfgTab: null,
  workingTab: "all",
  workingTabAuto: true,
  scheduleCount: null,
  pipeline: "idle",
  pipelineError: null,
  runInfo: null,
  execLogs: [],
  pdReplicate: false,
  pdSummary: { pids: 0, chained: 0 },
  // Ausgewaehlte Partner-Directory-Kennungen (Pids), die mit-transportiert werden.
  // Setzt sich aus automatisch erkannten (aus BPMN + ProcessDirect-Reverse-Match)
  // und manuell hinzugefuegten Pids zusammen. Bei pdReplicate=false wird die
  // Liste ignoriert. Dedupliziert.
  pdSelectedPids: [],
  // Etappe C: Stufen-Filter je Pid. { [pid]: string[] } mit Stufen-Keys
  // ("qas", "preprod", "prod" - jeweils lowercase ohne Bindestrich, wie der Backend-
  // Normalisierer normStage erwartet). Fehlt der Eintrag fuer einen Pid, gelten
  // Backend-seitig alle Stufen ("*") - identisch zum Altverhalten. Wird beim Paket-
  // wechsel wie pdSelectedPids zurueckgesetzt.
  pdStagesByPid: {},
  pdEdgeByPid: {},
  // Explicit Cloud (null) is different from an untouched default. Keep decisions
  // when a PID/stage is temporarily deselected and when revisiting a package.
  pdEdgeChoicesByPid: {},
  pdEdgeModeByPid: {},
  pdEdgeDefaultsLocked: false,
  // Z4: Git-zugeordnete Edge-Laufzeiten je PID/Stufe aus GET package-partners
  pdGitEdgeTargets: {},
  // Z2/Z5: Git-zugeordnete PIDs mit Paketzugehörigkeit aus GET package-partners
  pdGitPackagePartners: null,
  releaseState: "idle",
  releaseCandidate: null,
  releaseError: null,
  finalized: false,
};

// Nur-Keys-Variante von vorwaertsStufen(state) - der Reducer darf keine
// selector-Objekte konstruieren, braucht aber die Vorwaerts-Stufen-Keys fuer die
// PD-Vorbelegung. Bewusst dieselbe Regel wie vorwaertsStufen (Ziel-Stufe bis
// PROD, nur Stufen mit erreichbarer Instanz).
function vorwaertsStufenKeysAus(space) {
  if (!space || !Array.isArray(space.stages) || !space.zielStufe) return [];
  const startIdx = space.stages.findIndex((st) => st.stageKey === space.zielStufe);
  if (startIdx < 0) return [];
  const out = [];
  for (let i = startIdx; i < space.stages.length; i++) {
    const st = space.stages[i];
    if (!st || !(st.instanceId || st.candidates?.length) || !st.stageKey) continue;
    out.push(st.stageKey);
  }
  return out;
}

// Schlüssel, auf die sich PD Auswahl und Edge Laufzeit beziehen: je Stufe, im Mehrzielmodus je gewähltem Ziel (targetKey).
function pdStageKeys(state) {
  return state.multiTarget ? vorwaertsAlleZiele(state).map(column => column.key) : vorwaertsStufenKeysAus(state.space);
}
// Edge Laufzeiten und ihre Vorbelegung gelten für alle Ziele, auch verdeckte (Vertrag V.10): die Payload hängt nicht
// von der Klickreihenfolge ab.
function pdEdgeKeys(state) {
  return pdStageKeys(state);
}
// Abgleich der Ziele (Vertrag V.12): wegfallende Schlüssel verschwinden aus PD Auswahl und Edge Strukturen, neue Ziele
// kommen in jede vorhandene PD Einschränkung hinzu (Standard: alle Ziele).
function pdSync(draft, before, after) {
  const valid = new Set(after), fresh = after.filter(key => !before.includes(key));
  const only = map => Object.fromEntries(Object.entries(map || {}).map(([pid, targets]) =>
    [pid, Object.fromEntries(Object.entries(targets || {}).filter(([key]) => valid.has(key)))]));
  const stages = {}, removed = [];
  for (const [pid, list] of Object.entries(draft.pdStagesByPid || {})) {
    const next = Array.isArray(list) ? [...list.filter(key => valid.has(key)), ...fresh.filter(key => !list.includes(key))] : list;
    // Eine leere Liste hieße „alle Stufen“: die PID wird stattdessen entfernt, wie bei TOGGLE_PD_STAGE.
    if (Array.isArray(next) && !next.length) removed.push(pid); else stages[pid] = next;
  }
  const selected = removed.length ? (draft.pdSelectedPids || []).filter(pid => !removed.includes(pid)) : draft.pdSelectedPids;
  const without = map => (removed.length && map ? Object.fromEntries(Object.entries(map).filter(([pid]) => !removed.includes(pid))) : map);
  return { ...draft, pdStagesByPid: stages, ...(removed.length ? { pdSelectedPids: selected, pdReplicate: selected.length > 0 } : {}),
    ...(draft.pdEdgeModeByPid ? { pdEdgeModeByPid: without(draft.pdEdgeModeByPid) } : {}), pdEdgeByPid: only(without(draft.pdEdgeByPid)),
    ...(draft.pdEdgeChoicesByPid ? { pdEdgeChoicesByPid: only(without(draft.pdEdgeChoicesByPid)) } : {}) };
}
// Instanz hinter einem solchen Schlüssel (Stufe oder Ziel).
function pdKeyInstance(state, key) {
  return state.multiTarget ? vorwaertsAlleZiele(state).find(column => column.key === key) : (state.space?.stages || []).find(item => item.stageKey === key);
}

// Vorbelegung: fuer alle Pids in list, die noch KEINEN Eintrag in stagesMap haben,
// den Default-Satz aller Vorwaerts-Stufen setzen. Bestehende (bewusst reduzierte)
// Auswahl wird NICHT ueberschrieben - sonst zerreisst jede erneute Response die
// Nutzerintention.
function pdStagesVorbelegen(stagesMap, list, defaults) {
  if (!Array.isArray(list) || !list.length) return stagesMap;
  if (!Array.isArray(defaults) || !defaults.length) return stagesMap;
  let out = null;
  for (const pid of list) {
    if (typeof pid !== "string" || !pid) continue;
    if (Array.isArray(stagesMap?.[pid])) continue;
    if (!out) out = { ...(stagesMap || {}) };
    out[pid] = [...defaults];
  }
  return out || stagesMap || {};
}

// Referenzen folgen den iFlows. Eine globale Referenz (Message Mapping, Script Collection,
// Value Mapping) reist nur mit, wenn mindestens einer der iFlows, die sie nutzen, selbst im
// Transport ist. Ein Value Mapping ohne iFlow-Verweis (herkunft "paket") hat keine Nutzer und
// faellt unter "ohne bekannte Nutzer bleibt das bisherige Verhalten".
// Die Nutzer liefert package-references als referencedBy (Quell-Artefakt-Ids). Faellt der
// letzte nutzende iFlow raus - abgewaehlt, identisch, Entwurf -, faellt die Referenz mit;
// kommt er wieder dazu, greift die Vorbelegung nach Status. Ohne bekannte Nutzer bleibt
// das bisherige Verhalten. Eine bewusste Abwahl (refSel === false) ueberlebt, ein
// "nicht gebraucht" wird als fehlender Eintrag abgelegt, damit beides unterscheidbar bleibt.
const refKey = (r) => r?.bundleId || r?.id || r?.Id || r?.name || r?.Name;
const refIstEntwurf = (r) => r?.draft === true || String(r?.status || "").toLowerCase() === "draft" || String(r?.version || r?.Version || "").toLowerCase() === "draft";
// v10: package-references liefert den Referenztyp autoritativ aus dem iFlow-MANIFEST und meldet
// Referenzen, die sich per Schnittstelle nicht transportieren lassen (Function Libraries u. a.,
// transportierbar=false). Die bleiben sichtbar, kommen aber NIE in die Auswahl.
// DEV183: Fremde Referenzen (checkOnly) werden nur geprüft und sind wie nicht transportierbare nie wählbar.
const refNichtTransportierbar = (r) => r?.transportierbar === false || r?.checkOnly === true;
// Ein Value Mapping, das im Ziel schon existiert, ueberschreibt dort die gepflegten Werte
// (ueberschreibtZielwerte=true). Das darf keine Vorbelegung entscheiden - der Nutzer hakt es
// bewusst an. "Nicht vorbelegt" ist der fehlende Eintrag (undefined), nicht false: false ist
// die bewusste Abwahl, und die Referenz-folgt-iFlow-Ableitung unterscheidet beides.
const refUeberschreibtZiel = (r) => r?.ueberschreibtZielwerte === true;
export function gewaehlteIflows(sel) {
  const out = new Set();
  for (const [src, werke] of Object.entries(sel || {})) {
    if (Object.values(werke || {}).some(Boolean)) out.add(String(src));
  }
  return out;
}
export function refNutzer(r) {
  return Array.isArray(r?.referencedBy) ? r.referencedBy.map((x) => String(x || "")).filter(Boolean) : [];
}
export function refGebraucht(r, gewaehlt) {
  const nutzer = refNutzer(r);
  return nutzer.length ? nutzer.some((id) => gewaehlt.has(id)) : true;
}
function refSelNachIflows(refSel, globalRefs, sel) {
  const gewaehlt = gewaehlteIflows(sel);
  const out = { ...(refSel || {}) };
  for (const r of globalRefs || []) {
    const id = refKey(r);
    if (!id || !refNutzer(r).length) continue;
    if (r?.resolvable === false || refIstEntwurf(r) || refNichtTransportierbar(r)) continue;
    if (!refGebraucht(r, gewaehlt)) { delete out[id]; continue; }
    if (out[id] === undefined) {
      const vb = refVorbelegung(r);
      if (vb !== undefined) out[id] = vb;
    }
  }
  return out;
}
// Vorbelegung nach Status: identisch -> bewusst aus (false), sonst an (true). Ein Value Mapping,
// das Zielwerte ueberschreiben wuerde, bleibt ohne Eintrag (undefined) - der Nutzer entscheidet.
function refVorbelegung(r) {
  if (needsSameReferenceVersionPermission(r)) return false;
  if (refUeberschreibtZiel(r)) return undefined;
  return true;
}

const draftKeys = ["pkgId", "pkgName", "pkgVersion", "pkgMod", "precheckByBucket", "precheckState", "globalRefs", "refSel", "artifactDeselection", "globalRefDeselection", "orphanSel", "expanded", "sel", "deploy", "refDeploy", "cfg", "cfgErrors", "gitCfg", "gitCfgState", "gitCfgUnvollstaendig", "cfgTab", "workingTab", "workingTabAuto", "pdReplicate", "pdSummary", "pdSelectedPids", "pdStagesByPid", "pdEdgeByPid", "pdPrefillKey", "packagePrepared", "configReviewed"];
draftKeys.push("precheckRequest", "precheckToken", "precheckPhases", "precheckError", "precheckRefsWarning", "precheckReleasedId");
draftKeys.push("plantChangeConfirmations");
draftKeys.push("cfgSuggestionUndo");
draftKeys.push("configReadGeneration");
draftKeys.push("gitCfgScope");
draftKeys.push("pdEdgeChoicesByPid", "pdEdgeDefaultsLocked", "pdEdgeModeByPid");
draftKeys.push("pdGitEdgeTargets", "pdGitPackagePartners");
draftKeys.push("manualEdgeDeploy", "deployStages", "manualEdgeStages");
draftKeys.push("packageDeploy", "packageDeployStages", "designEdgeDeploy");
const saveDraft = (state) => Object.fromEntries(draftKeys.map(key => [key, state[key] ?? initialState[key]]));
const emptyDraft = (pkg) => ({ ...saveDraft(initialState), pkgId: pkg?.id || null, pkgName: pkg?.name || pkg?.id || null, pkgVersion: pkg?.version || null, pkgMod: pkg?.mod || null });
const packageEdits = new Set(["SET_SEL", "TOGGLE_SEL", "SET_DEPLOY", "SET_REF_DEPLOY", "TOGGLE_REF", "SET_PRECHECK", "SET_GLOBAL_REFS", "SET_CFG", "PATCH_CFG_ENTRY", "SET_PD_REPLICATE", "SET_PD_SELECTED_PIDS", "TOGGLE_PD_PID", "TOGGLE_PD_STAGE", "SET_PD_STAGES", "SET_PD_EDGE"]);
packageEdits.add("TOGGLE_CFG_SUGGESTIONS");
packageEdits.add("SET_MANUAL_EDGE_DEPLOY");
packageEdits.add("SET_DEPLOY_STAGES");
packageEdits.add("SET_PACKAGE_DEPLOY");
packageEdits.add("SET_PACKAGE_DEPLOY_STAGES");
packageEdits.add("SET_DESIGN_EDGE_DEPLOY");
packageEdits.add("SET_PD_EDGE_MODE");
packageEdits.add("TOGGLE_STAGE_TARGET");

export function packageStates(state) {
  return (state.selectedPackages || []).map(pkg => ({ ...state, ...(pkg.id === state.pkgId ? saveDraft(state) : state.packageDrafts?.[pkg.id] || emptyDraft(pkg)) }));
}

export function reducer(state, action) {
  if ((state.batch || state.batchRequest) && ["TOGGLE_SEL", "SET_SEL", "TOGGLE_REF", "TOGGLE_WERK", "SET_ALL_WERKE", "SET_DEPLOY", "SET_REF_DEPLOY", "SET_MANUAL_EDGE_DEPLOY", "SET_DEPLOY_STAGES", "TOGGLE_STAGE_TARGET"].includes(action.type)) return state;
  if (action.type === "SET_SAME_REFERENCE_VERSION_PERMISSION") {
    const allowed = action.payload === true;
    if (allowed === state.canTransportSameVersionReferences) return state;
    const update = draft => {
      const refSel = { ...draft.refSel };
      if (!allowed) for (const ref of draft.globalRefs || []) {
        if (needsSameReferenceVersionPermission(ref)) refSel[refKey(ref)] = false;
      }
      return { ...draft, refSel, packagePrepared: false, configReviewed: false };
    };
    // Cached but currently deselected packages must not restore a revoked choice.
    const drafts = Object.fromEntries(Object.entries(state.packageDrafts || {}).map(([id, draft]) => [id, saveDraft(update(draft))]));
    const current = update(state);
    if (state.pkgId) drafts[state.pkgId] = saveDraft(current);
    return { ...current, canTransportSameVersionReferences: allowed, packageDrafts: drafts };
  }
  if (action.type === "SET_SAME_VERSION_PERMISSION") {
    const allowed = action.payload === true;
    if (allowed === state.canTransportSameVersion) return state;
    const update = draft => {
      const sel = Object.fromEntries(Object.entries(draft.sel || {}).map(([id, choices]) => [id, { ...choices }]));
      // Pakettransport: das ganze Paket reist mit, auch gleiche Versionen. Die Berechtigung ändert die Auswahl nicht.
      if (!allowed && !isPackageTransport(draft)) for (const entry of Object.values(draft.precheckByBucket || {})) for (const a of entry.comparison || []) {
        if (needsSameVersionPermission(a) && sel[a.originalId || a.Id]) delete sel[a.originalId || a.Id][entry.werk?.kennung || "std"];
      }
      return { ...draft, sel, refSel: refSelNachIflows(draft.refSel, draft.globalRefs, sel), packagePrepared: false, configReviewed: false };
    };
    const drafts = Object.fromEntries(packageStates(state).map(draft => [draft.pkgId, saveDraft(update(draft))]));
    return { ...update(state), canTransportSameVersion: allowed, packageDrafts: drafts };
  }
  // Background comparisons are bound to their package and landscape, not the visible tab.
  if (action.type.startsWith("PACKAGE_PRECHECK_")) {
    const { pkgId, generation, token } = action;
    if (generation !== state.precheckGeneration || state.batch || state.batchRequest) return state;
    const pkg = state.selectedPackages.find(p => p.id === pkgId);
    if (!pkg) return state;
    const draft = pkgId === state.pkgId ? saveDraft(state) : state.packageDrafts[pkgId] || emptyDraft(pkg);
    let next = draft;
    if (action.type === "PACKAGE_PRECHECK_REQUEST") {
      next = { ...draft, precheckRequest: draft.precheckRequest + 1, precheckToken: null,
        precheckState: "queued", precheckError: null, precheckPhases: [], packagePrepared: false, plantChangeConfirmations: [],
        precheckReleasedId: action.releasedId || draft.precheckReleasedId };
    } else if (action.type === "PACKAGE_PRECHECK_START") {
      next = { ...draft, precheckToken: token, precheckState: "running", precheckError: null, precheckPhases: [] };
    } else {
      if (draft.precheckToken !== token || draft.precheckState !== "running") return state;
      if (action.type === "PACKAGE_PRECHECK_PROGRESS") next = { ...draft, precheckPhases: action.phases };
      if (action.type === "PACKAGE_PRECHECK_ERROR") next = { ...draft, precheckState: "error", precheckError: action.error || true, precheckPhases: [] };
      if (action.type === "PACKAGE_PRECHECK_DONE") {
        const { precheck, references, referencesIncomplete } = action.result;
        const sel = {}, expanded = {};
        const hadComparison = Object.keys(draft.precheckByBucket).length > 0;
        const whole = isPackageTransport({ precheckByBucket: precheck });
        for (const entry of Object.values(precheck)) for (const a of entry.comparison || []) {
          const id = a.originalId || a.Id, werk = entry.werk?.kennung || "std";
          if (!sel[id]) sel[id] = {};
          expanded[id] = true;
          // Pakettransport: jede freigegebene Version ist fest ausgewählt (Entwürfe ohne Deployment).
          if (whole) { if (!isArtifactDraft(a)) sel[id][werk] = true; continue; }
          const selected = !hadComparison || draft.precheckReleasedId === id || !Object.prototype.hasOwnProperty.call(draft.sel, id) || draft.sel[id]?.[werk];
          if ((!needsSameVersionPermission(a) || (state.canTransportSameVersion && wasSameVersionSelected(draft.precheckByBucket, draft.sel, a, werk))) && !isArtifactDraft(a) && selected) sel[id][werk] = true;
        }
        next = saveDraft(coreReducer({ ...draft, sel, canTransportSameVersionReferences: state.canTransportSameVersionReferences }, { type: "SET_GLOBAL_REFS", payload: references }));
        next = { ...next, precheckByBucket: precheck, expanded, precheckState: referencesIncomplete ? "partial" : "done",
          precheckRefsWarning: !!referencesIncomplete, precheckError: null, precheckReleasedId: null,
          precheckPhases: [], packagePrepared: false, configReviewed: false, plantChangeConfirmations: [] };
      }
    }
    return { ...state, ...(pkgId === state.pkgId ? next : {}), packageDrafts: { ...state.packageDrafts, [pkgId]: next } };
  }
  // A response from an earlier package visit must never change the current draft,
  // including when the user has switched away and back to the same package.
  if (action.scope && (action.scope.pkgId !== state.pkgId || action.scope.epoch !== state.packageEpoch)) return state;
  if (action.type === "LOCK_PD_EDGE_DEFAULTS") return { ...state, pdEdgeDefaultsLocked: true };
  if (action.type === "UNLOCK_PD_EDGE_DEFAULTS" && action.payload === true && !state.batch && !state.batchRequest)
    return { ...state, pdEdgeDefaultsLocked: false };
  if (action.type === "APPLY_PD_EDGE_DEFAULTS") {
    if (state.batch || state.batchRequest || state.pdEdgeDefaultsLocked || (state.pipeline && state.pipeline !== "idle")) return state;
    let next = withPdEdgeDefaults(state, action.payload);
    const drafts = { ...state.packageDrafts };
    let changed = next !== state;
    for (const draft of packageStates(state)) {
      const updated = draft.pkgId === state.pkgId ? next : withPdEdgeDefaults(draft, action.payload);
      if (updated !== draft && (draft.pkgId !== state.pkgId || next !== state)) {
        drafts[draft.pkgId] = saveDraft(updated);
        changed = true;
      }
    }
    return changed ? { ...next, packageDrafts: drafts } : state;
  }
  if (action.type === "SET_PLANT_NAMING") {
    if (state.batch || state.batchRequest) return state;
    const naming = action.payload;
    if (!naming || !["auto", "generic", "existing"].includes(naming.mode)) return state;
    if (naming.mode === "existing" && (!usesWerke(state) || (state.selectedPackages || []).length > 1 || typeof naming.sourcePlant !== "string")) return state;
    const plantNaming = naming.mode === "existing" ? { mode: "existing", sourcePlant: naming.sourcePlant } : { mode: naming.mode };
    if (state.plantNaming?.mode === plantNaming.mode && state.plantNaming?.sourcePlant === plantNaming.sourcePlant) return state;
    return invalidateNaming(state, plantNaming);
  }
  if (action.type === "SET_ALL_DEPLOY" || action.type === "SET_ALL_MANUAL_EDGE_DEPLOY") {
    if (state.batch || state.batchRequest || typeof action.payload !== "boolean") return state;
    const isEdge = action.type === "SET_ALL_MANUAL_EDGE_DEPLOY";
    const field = isEdge ? "manualEdgeDeploy" : "deploy";
    const update = draft => {
      // SET_ALL_DEPLOY: Pakettransport bleibt unverändert (CI ist paketglobal, SET_PACKAGE_DEPLOY).
      // SET_ALL_MANUAL_EDGE_DEPLOY: Pakettransport ab v27 erlaubt Edge je iFlow, die Sammelschaltung greift.
      if (isPackageTransport(draft) && !isEdge) return draft;
      const choicesById = { ...draft[field] };
      const stageField = field === "deploy" ? "deployStages" : "manualEdgeStages";
      const overrides = { ...draft[stageField] };
      for (const [id, choices] of Object.entries(draft.sel || {})) for (const plant of buckets(draft)) {
        const key = choices[plant.key] ? plant.key : plant.kennung;
        if (key && choices[key] && !selectedArtifactIsDraft(draft, id, key)) {
          choicesById[id] = { ...choicesById[id], [key]: action.payload };
          overrides[id] = { ...overrides[id], [key]: {} };
        }
      }
      return { ...draft, [field]: choicesById, [stageField]: overrides, packagePrepared: false, configReviewed: false };
    };
    const drafts = Object.fromEntries(packageStates(state).map(draft => [draft.pkgId, saveDraft(update(draft))]));
    return { ...update(state), packageDrafts: { ...state.packageDrafts, ...drafts } };
  }
  if (action.type === "SET_BATCH") return { ...state, batch: action.payload };
  if (action.type === "SET_BATCH_REQUEST") return { ...state, batchRequest: action.payload };
  if (action.type === "SET_CONFIG_REVIEWED") return { ...state, configReviewed: true };
  if (action.type === "CONFIRM_PLANT_CHANGES") return { ...state, plantChangeConfirmations: action.payload, packagePrepared:false };
  if (action.type === "SET_PACKAGE_PREPARED") return { ...state, packagePrepared: !!action.payload };
  if (action.type === "SET_PD_PREFILL_KEY") return { ...state, pdPrefillKey: action.payload };
  if (action.type === "SET_PRECHECK_STATE") return { ...state, precheckState: action.payload };
  if (action.type === "SET_CFG_ERROR") {
    const { key, error } = action.payload;
    return { ...state, cfgErrors: { ...state.cfgErrors, [key]: error }, packagePrepared: false };
  }
  if (action.type === "RETRY_CONFIGS") return { ...state, cfgErrors: {}, gitCfgState: "idle", configReadGeneration: (state.configReadGeneration || 0) + 1, configReviewed: false, packagePrepared: false };
  if (action.type === "TOGGLE_PACKAGE" || action.type === "ACTIVATE_PACKAGE") {
    if (state.batch || state.batchRequest) return state;
    const pkg = action.payload;
    let selected = state.selectedPackages || [];
    if (action.type === "TOGGLE_PACKAGE" && state.plantNaming?.mode === "existing"
      && selected.length && !selected.some(p => p.id === pkg.id)) return state;
    const drafts = { ...state.packageDrafts, ...(state.pkgId ? { [state.pkgId]: saveDraft(state) } : {}) };
    if (action.type === "TOGGLE_PACKAGE") {
      selected = selected.some(p => p.id === pkg.id) ? selected.filter(p => p.id !== pkg.id) : [...selected, pkg];
      // Removing an inactive package does not disturb the form being edited.
      if (state.pkgId && selected.some(p => p.id === state.pkgId)) return { ...state, selectedPackages: selected, packageDrafts: drafts };
    }
    const active = action.type === "ACTIVATE_PACKAGE" ? selected.find(p => p.id === pkg.id) : selected[0];
    if (action.type === "ACTIVATE_PACKAGE" && (!active || active.id === state.pkgId)) return state;
    const draft = active ? drafts[active.id] || emptyDraft(active) : emptyDraft(null);
    // An interrupted preload has no reader left. Re-entering restarts it.
    const next = { ...state, ...draft, gitCfgState: draft.gitCfgState === "laedt" ? "idle" : draft.gitCfgState,
      selectedPackages: selected, packageDrafts: drafts, packageEpoch: (state.packageEpoch || 0) + 1,
      step: action.type === "ACTIVATE_PACKAGE" ? 3 : state.step };
    // The identifier belongs to the selected source package, never to its successor.
    return state.plantNaming?.mode === "existing" && state.pkgId && state.pkgId !== active?.id
      ? invalidateNaming(next, { mode: "existing", sourcePlant: "" }) : next;
  }
  if (["TOGGLE_WERK", "SET_ALL_WERKE"].includes(action.type) && state.selectedPackages?.length) {
    const next = coreReducer(state, action);
    const drafts = Object.fromEntries(state.selectedPackages.map(pkg => [pkg.id, emptyDraft(pkg)]));
    return { ...next, ...drafts[state.pkgId], plantNaming: usesWerke(next) ? state.plantNaming : DEFAULT_PLANT_NAMING,
      packageDrafts: drafts, packageEpoch: (state.packageEpoch || 0) + 1,
      precheckGeneration: (state.precheckGeneration || 0) + 1 };
  }
  if (["SET_PD_EDGE_MODE", "SET_PD_EDGE"].includes(action.type)) {
    if (state.batch || state.batchRequest || state.pdEdgeDefaultsLocked || (state.pipeline && state.pipeline !== "idle")) return state;
    const next = coreReducer(state, action);
    if (next === state) return state;
    const drafts = { ...state.packageDrafts };
    for (const draft of packageStates(state)) {
      if (!(draft.pdSelectedPids || []).includes(action.payload?.pid)) continue;
      const updated = coreReducer(draft, action);
      drafts[draft.pkgId] = saveDraft({ ...updated, pdEdgeByPid: pdEdgeSelection(updated), packagePrepared: false });
    }
    return { ...next, pdEdgeByPid: pdEdgeSelection(next), packageDrafts: drafts, packagePrepared: false };
  }
  let next = coreReducer(state, action);
  if (next !== state && action.type === "SET_STAGE_TARGETS") {
    const before = pdStageKeys(state), after = pdStageKeys(next);
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      const drafts = Object.fromEntries(Object.entries(state.packageDrafts || {}).map(([id, draft]) => [id, saveDraft(pdSync(draft, before, after))]));
      next = { ...pdSync(next, before, after), packageDrafts: drafts };
    }
  }
  if (next !== state && action.type.includes("PD_")) next = { ...next, pdEdgeByPid: pdEdgeSelection(next) };
  if (next === state) return state;
  if (action.type === "PICK_PACKAGE") return invalidateNaming({ ...next, selectedPackages: [action.payload] },
    state.plantNaming?.mode === "existing" ? { mode: "existing", sourcePlant: "" } : DEFAULT_PLANT_NAMING);
  if (["TOGGLE_WERK", "SET_ALL_WERKE"].includes(action.type) && !usesWerke(next)) return { ...next, plantNaming: DEFAULT_PLANT_NAMING };
  if (action.type === "PICK_SPACE" || action.type === "RESET_ALL") return { ...next, packageEpoch: (state.packageEpoch || 0) + 1,
    precheckGeneration: (state.precheckGeneration || 0) + 1 };
  if (packageEdits.has(action.type)) return { ...next, packagePrepared: false,
    plantChangeConfirmations: ["SET_SEL", "TOGGLE_SEL", "SET_PRECHECK"].includes(action.type) ? [] : next.plantChangeConfirmations,
    configReviewed: ["SET_SEL", "TOGGLE_SEL", "SET_PRECHECK", "SET_CFG", "PATCH_CFG_ENTRY", "TOGGLE_CFG_SUGGESTIONS"].includes(action.type) ? false : next.configReviewed };
  return next;
}

// Werkskatalog (Welle 4, W4.4): belegt die Ziele je Stufe aus dem Katalog der gewählten Werke vor. Nur im Mehrzielmodus
// und nur mit gelieferten plantTargets; sonst bleibt der Zustand unverändert. Es wird nichts gemerkt.
export function baseStageOf(space) {
  const list = space?.stages || [];
  return list.slice(Math.max(0, list.findIndex(st => st.stageKey === space?.zielStufe)))
    .find(st => st.instanceId || st.candidates?.length)?.stageKey || null;
}
function applyPlantPreset(state, previous = null) {
  if (!state.multiTarget || !state.stageTargets || !state.plantTargets) return state;
  const names = target => (target.space?.werkeList || []).filter(w => target.werkOn?.[w.key]).map(w => w.kennung);
  const baseStage = baseStageOf(state.space);
  const run = kennungen => plantTargetPreset({ stageTargets: state.stageTargets, plantTargets: state.plantTargets, kennungen, baseStage });
  const next = run(names(state));
  // Nur die Differenz zwischen alter und neuer Vereinigung wirkt: manuelle Änderungen des Benutzers bleiben erhalten.
  const old = run(names(previous || { ...state, werkOn: {} })).selection;
  const merged = {};
  for (const [stage, rows] of Object.entries(state.stageTargets)) {
    if (!next.selection[stage]) continue;
    const added = next.selection[stage].filter(id => !(old[stage] || []).includes(id));
    const dropped = (old[stage] || []).filter(id => !next.selection[stage].includes(id));
    const current = state.stageTargetSel?.[stage] || [];
    merged[stage] = rows.map(row => row.targetId).filter(id => (current.includes(id) || added.includes(id)) && !dropped.includes(id));
  }
  return { ...state, stageTargetSel: normalizeSelection(state.stageTargets, merged, baseStage), plantTargetSource: Object.keys(next.source).length ? next.source : null };
}

function invalidateNaming(state, plantNaming) {
  const drafts = Object.fromEntries((state.selectedPackages || []).map(pkg => [pkg.id, emptyDraft(pkg)]));
  const current = drafts[state.pkgId] || emptyDraft(state.pkgId ? { id: state.pkgId, name: state.pkgName } : null);
  return { ...state, ...current, plantNaming, packageDrafts: drafts,
    packageEpoch: (state.packageEpoch || 0) + 1, precheckGeneration: (state.precheckGeneration || 0) + 1 };
}

function coreReducer(state, action) {
  switch (action.type) {
    case "SET_TOPIC":
      if (!action.payload.spaceId || action.payload.spaceId !== canonicalTopicSpace(state)) return state;
      return { ...state, topicId: action.payload.topicId || null, topicSpaceId: action.payload.spaceId,
        topicSelection: action.payload.topicSelection || (action.payload.topicId ? "topic" : null), topicSourceInstanceId: state.sourceInstanceId };
    case "SET_STEP": {
      let ziel = action.payload;
      if (ziel >= 3 && (!validPlantNaming(state.plantNaming)
        || (state.plantNaming?.mode === "existing" && state.selectedPackages?.length !== 1))) return state;
      // Ohne gepflegte Werke ist Schritt 1 (Landschaften) nur eine Wiederholung der Space-Karte:
      // er wird uebersprungen - vorwaerts nach 2, rueckwaerts nach 0. Zentral hier, damit jeder
      // Absender (Weiter, Zurueck, Stepper-Klick) dieselbe Regel bekommt.
      if (ziel === 1 && !landschaftsSchrittNoetig(state)) ziel = (state.step || 0) < 1 ? 2 : 0;
      return { ...state, step: ziel };
    }
    case "PICK_SPACE": {
      const { spaceKey } = action.payload;
      const { sourceInstance, prodInstance } = action.payload;
      // Stufen mit mehreren Instanzen tragen `candidates`; erst das gelesene Standardziel (SET_STAGE_TARGETS) bestimmt
      // Instanz und Ziel, vorher gibt es keine Erstwahl (Vertrag V.12).
      const resolved = applyStageTargets({ ...action.payload.space, qasInstance: action.payload.qasInstance }, null);
      const space = resolved.stages === action.payload.space?.stages ? action.payload.space : { ...action.payload.space, stages: resolved.stages };
      const qasInstance = resolved.qasInstance;
      const werkeList = qasInstance ? extractWerkeFromInstance(qasInstance) : [];
      const hasCandidates = (space?.stages || []).some(st => st.candidates?.length);
      // Stufen mit Kandidaten zählen mit ihren Kandidaten, nicht mit der (inzwischen gelesenen) Instanz: ein erneuter
      // Klick auf denselben Space setzt den Wizard nicht zurück.
      const signature = list => JSON.stringify((list || []).map(st => [st.stageKey, st.candidates?.length ? st.candidates.map(c => c.id) : st.instanceId]));
      const changed = state.spaceKey !== spaceKey || canonicalTopicSpace(state) !== (space?.mirrorOf || space?.id || "")
        || state.sourceInstanceId !== (sourceInstance?.id || null)
        || (!hasCandidates && ((state.space?.qasInstance?.id || null) !== (qasInstance?.id || null)
          || JSON.stringify(state.space?.werkeList || []) !== JSON.stringify(werkeList)))
        || signature(state.space?.stages) !== signature(space?.stages);
      if (!changed) return state;
      const werkOn = Object.fromEntries(werkeList.map(w => [w.key, false]));
      return {
        ...initialState,
        canTransportSameVersion: state.canTransportSameVersion === true,
        canTransportSameVersionReferences: state.canTransportSameVersionReferences === true,
        scheduleCount: state.scheduleCount,
        spaceKey,
        spaceId: space?.id || null,
        space: { ...space, sourceInstance, qasInstance, prodInstance, werkeList },  // space traegt stufen/quellStufe/zielStufe aus Scene0
        sourceInstanceId: sourceInstance?.id || null,
        targetInstanceIds: qasInstance ? [qasInstance.id] : [],
        werkOn,
      };
    }
    case "SET_STAGE_TARGETS": {
      // payload: { multiTarget, stageTargets } aus GET /api/cicd/stage-branches/config, nur nach erfolgreichem Lesen
      // (Vertrag V.12). Fehlt der Mehrzielmodus, gilt wie bisher die erste Instanz einer Stufe.
      const multi = action.payload?.multiTarget === true && !!action.payload.stageTargets;
      const stageTargets = multi ? action.payload.stageTargets : null;
      const space = applyStageTargets(state.space, stageTargets, { fallbackFirst: true });
      const baseStage = (space?.stages || []).slice(Math.max(0, (space?.stages || []).findIndex(st => st.stageKey === space?.zielStufe)))
        .find(st => st.instanceId || st.candidates?.length)?.stageKey || null;
      const stageTargetSel = multi ? normalizeSelection(stageTargets, state.stageTargetSel, baseStage) : {};
      const catalog = multi && action.payload.plantTargets && typeof action.payload.plantTargets === "object" && Object.keys(action.payload.plantTargets).length
        ? action.payload.plantTargets : null;
      const sig = value => JSON.stringify([value.stageTargets, value.stageTargetSel, value.space?.stages?.map(st => st.instanceId), value.stageTargetsRead, value.plantTargets ?? null]);
      // Der Katalog wird je Transport bei der ersten Lesung eingefroren; spätere Config Lesungen ändern ihn nicht.
      const freeze = !!catalog && !state.plantTargets;
      const catalogKeys = !multi && state.plantTargets ? { plantTargets: null, plantTargetSource: null } : freeze ? { plantTargets: catalog } : {};
      const nextState = { ...state, multiTarget: multi, stageTargets, stageTargetSel, stageTargetsRead: true, ...catalogKeys };
      if (sig({ ...nextState, space }) === sig(state) && multi === state.multiTarget) return state;
      const qasChanged = (space?.qasInstance?.id || null) !== (state.space?.qasInstance?.id || null);
      const werkeList = qasChanged && space?.qasInstance ? extractWerkeFromInstance(space.qasInstance) : space?.werkeList;
      const applied = { ...nextState,
        space: space ? { ...space, werkeList } : space,
        ...(qasChanged ? { targetInstanceIds: space?.qasInstance ? [space.qasInstance.id] : [],
          werkOn: Object.fromEntries((werkeList || []).map(w => [w.key, false])) } : {}) };
      // Nur beim ersten Lesen des Katalogs: schon gewählte Werke belegen vor. Spätere Lesungen verwerfen keine Handauswahl.
      return freeze && !state.batch && !state.batchRequest ? applyPlantPreset(applied) : applied;
    }
    case "TOGGLE_STAGE_TARGET": {
      const { stage, targetId } = action.payload || {};
      const rows = state.stageTargets?.[stage];
      if (!state.multiTarget || stage === "dev" || !rows?.some(row => row.targetId === targetId)) return state;
      const current = state.stageTargetSel?.[stage] || [];
      const on = current.includes(targetId);
      if (on && current.length <= 1) return state; // Je Stufe bleibt mindestens ein Ziel.
      // Die Basisspalte (Standardziel der ersten Vorwärtsstufe) bleibt immer gewählt (Vertrag V.16).
      const first = vorwaertsStufen(state)[0]?.key;
      const standard = (rows.find(row => row.isDefault) || rows[0]).targetId;
      if (on && stage === first && targetId === standard) return state;
      const next = rows.map(row => row.targetId).filter(id => id === targetId ? !on : current.includes(id));
      return { ...state, stageTargetSel: { ...state.stageTargetSel, [stage]: next } };
    }
    case "SET_ALL_WERKE":
    case "TOGGLE_WERK": {
      const plants = allWerke(state);
      if (action.type === "TOGGLE_WERK" && !plants.some(w => w.key === action.payload)) return state;
      if (action.type === "SET_ALL_WERKE" && typeof action.payload !== "boolean") return state;
      const next = action.type === "SET_ALL_WERKE"
        ? Object.fromEntries(plants.map(w => [w.key, action.payload]))
        : { ...state.werkOn, [action.payload]: !state.werkOn[action.payload] };
      // gitCfg depends on the plant selection: a newly enabled plant has its own file in Git that
      // is not loaded yet. Keeping the old state would leave the new plant without its maintained
      // values and overwrite them on the next snapshot.
      // A3: Getippte Werte für weiterhin ausgewählte Werke erhalten. cfgInvalidated nur,
      // wenn tatsächlich getippte Werte verworfen wurden (entfernte Werke hatten modified).
      const removedWerke = plants.filter(w => state.werkOn[w.key] && !next[w.key]).map(w => w.key);
      const keptCfg = {};
      for (const [k, v] of Object.entries(state.cfg || {})) {
        if (!removedWerke.includes(k)) keptCfg[k] = v;
      }
      const hadModified = removedWerke.some(k => Object.values(state.cfg?.[k] || {}).some(art =>
        (art?.entries || []).some(e => e?.modified)));
      return applyPlantPreset({ ...state, werkOn: next, workingTab: "all", precheckByBucket: {}, sel: {}, deploy: {}, manualEdgeDeploy: {}, deployStages: {}, manualEdgeStages: {}, refDeploy: {}, cfg: keptCfg, cfgSuggestionUndo: null, cfgTab: null, gitCfg: {}, gitCfgState: "idle", cfgInvalidated: hadModified || false }, state);
    }
    case "PICK_PACKAGE": {
      const { id, name, version, mod } = action.payload;
      const changed = state.pkgId !== id;
      if (!changed) return state;
      return {
        ...state,
        pkgId: id,
        pkgName: name || id,
        pkgVersion: version || null,
        pkgMod: mod || null,
        precheckByBucket: {},
        globalRefs: [],
        refSel: {},
        sel: {},
        deploy: {},
        manualEdgeDeploy: {},
        deployStages: {},
        manualEdgeStages: {},
        refDeploy: {},
        cfg: {},
        cfgSuggestionUndo: null,
        gitCfg: {},
        gitCfgState: "idle",
        cfgTab: null,
        // A3: cfgInvalidated nur wenn tatsächlich getippte Werte verloren gehen.
        cfgInvalidated: Object.values(state.cfg || {}).some(werk =>
          Object.values(werk || {}).some(art => (art?.entries || []).some(e => e?.modified))),
        workingTab: "all",
        artifactDeselection: {},
        globalRefDeselection: {},
        orphanSel: {},
        // Erkannte Pids gehoeren zum konkreten Paket. Wenn das Paket wechselt,
        // muss die Auswahl neu aufgebaut werden - sonst wandern PD-Kennungen
        // ins falsche Paket.
        pdSelectedPids: [],
        // Stufen-Filter je Pid gehoert genauso zum Paket - alter Zustand darf nicht
        // die Vorbelegung im neuen Paket ueberschreiben.
        pdStagesByPid: {},
      };
    }
    case "SET_PRECHECK":
      return { ...state, precheckByBucket: action.payload || {} };
    case "SET_GLOBAL_REFS": {
      const list = Array.isArray(action.payload) ? action.payload : [];
      // Schluessel der NEUEN Liste (Id vor Name, wie unten). Auswahl-Eintraege, die hier nicht
      // mehr vorkommen, fallen raus: ein Reload kann eine Referenz entfernen, die alte Auswahl
      // haette sie weiter mitgeschickt - ohne Treffer in globalRefs ginge sie mit leerem Typ
      // in den Snapshot und der bricht mit 404 ab.
      const gueltig = new Set();
      for (const r of list) {
        const key = r?.bundleId || r?.id || r?.Id || r?.name || r?.Name;
        if (key) gueltig.add(key);
      }
      const refSel = {};
      for (const [k, v] of Object.entries(state.refSel || {})) {
        if (gueltig.has(k)) refSel[k] = v;
      }
      for (const r of list) {
        // Id VOR Name (der Snapshot laedt per Id) - identische Ableitung wie in Scene3/Scene5,
        // sonst laufen die refSel-Schluessel zwischen Vorauswahl und Anzeige auseinander.
        const id = r?.bundleId || r?.id || r?.Id || r?.name || r?.Name;
        if (!id) continue;
        // Nicht aufloesbare Referenz (Backend: resolvable=false) darf NIE in die
        // Transport-Auswahl geraten. Wir setzen die Auswahl hart auf false und
        // ueberschreiben eine ggf. schon existierende Vorbelegung - der Anwender
        // koennte diese Referenz nie mit einem sinnvollen Ergebnis mit-transportieren.
        if (r?.resolvable === false) {
          refSel[id] = false;
          continue;
        }
        // Entwuerfe genauso hart ausschliessen: ohne aktive Version kann der Snapshot
        // die Referenz nicht laden und wuerde den ganzen Transport abbrechen.
        const status = String(r?.status || "").toLowerCase();
        const draft = r?.draft === true || status === "draft" || String(r?.version || r?.Version || "").toLowerCase() === "draft";
        if (draft) {
          refSel[id] = false;
          continue;
        }
        // Per Schnittstelle nicht transportierbar (Function Library u. a.): wird gemeldet,
        // aber nie transportiert - Auswahl hart aus, auch gegen eine alte Vorbelegung.
        if (refNichtTransportierbar(r)) {
          refSel[id] = false;
          continue;
        }
        if (needsSameReferenceVersionPermission(r)) {
          const previous = (state.globalRefs || []).find(old => refKey(old) === id);
          refSel[id] = state.canTransportSameVersionReferences === true && refSel[id] === true && sameReferenceVersionPair(previous, r);
          continue;
        }
        if (refSel[id] === undefined) {
          // Ein Value Mapping mit ueberschreibtZielwerte bleibt hier ohne Eintrag: nicht
          // vorbelegt, damit der Nutzer die Freigabe bewusst je Zeile trifft.
          const vb = refVorbelegung(r);
          if (vb !== undefined) refSel[id] = vb;
        }
      }
      return { ...state, globalRefs: list, refSel: refSelNachIflows(refSel, list, state.sel) };
    }
    case "SET_SEL": {
      const sel = Object.fromEntries(Object.entries(action.payload || {}).map(([id, choices]) => [id, { ...choices }]));
      const whole = isPackageTransport(state);
      for (const entry of Object.values(state.precheckByBucket || {})) for (const a of entry.comparison || []) {
        if ((isArtifactDraft(a) || (!whole && !state.canTransportSameVersion && needsSameVersionPermission(a))) && sel[a.originalId || a.Id]) delete sel[a.originalId || a.Id][entry.werk?.kennung || "std"];
      }
      return { ...state, sel, refSel: refSelNachIflows(state.refSel, state.globalRefs, sel) };
    }
    case "TOGGLE_SEL": {
      // Pakettransport: Zeilen sind fest ausgewählt und lassen sich nicht umschalten.
      if (isPackageTransport(state)) return state;
      const { sourceId, werkKey, on } = action.payload;
      const cur = { ...(state.sel[sourceId] || {}) };
      const comparison = Object.values(state.precheckByBucket || {}).flatMap(entry => (entry.comparison || []).map(a => ({ a, key: entry.werk?.kennung || "std" })))
        .find(({ a, key }) => (a.originalId || a.Id) === sourceId && key === werkKey)?.a;
      if (on && comparison && (isArtifactDraft(comparison) || (needsSameVersionPermission(comparison) && !state.canTransportSameVersion))) return state;
      if (on) cur[werkKey] = true;
      else delete cur[werkKey];
      const next = { ...state.sel, [sourceId]: cur };
      return { ...state, sel: next, refSel: refSelNachIflows(state.refSel, state.globalRefs, next) };
    }
    case "SET_DEPLOY": {
      const { sourceId, werkKey, on } = action.payload || {};
      // Pakettransport: CI ist paketglobal, nicht je Zeile (SET_PACKAGE_DEPLOY).
      if (isPackageTransport(state)) return state;
      if (!state.sel?.[sourceId]?.[werkKey] || typeof on !== "boolean" || selectedArtifactIsDraft(state, sourceId, werkKey)) return state;
      return { ...state, deploy: { ...state.deploy, [sourceId]: { ...state.deploy?.[sourceId], [werkKey]: on } },
        deployStages: { ...state.deployStages, [sourceId]: { ...state.deployStages?.[sourceId], [werkKey]: {} } } };
    }
    case "SET_MANUAL_EDGE_DEPLOY": {
      const { sourceId, werkKey, on } = action.payload || {};
      // Pakettransport ab v27: Edge je iFlow ist frei wählbar.
      if (!state.sel?.[sourceId]?.[werkKey] || typeof on !== "boolean" || selectedArtifactIsDraft(state, sourceId, werkKey)) return state;
      return { ...state, manualEdgeDeploy: { ...state.manualEdgeDeploy, [sourceId]: { ...state.manualEdgeDeploy?.[sourceId], [werkKey]: on } },
        manualEdgeStages: { ...state.manualEdgeStages, [sourceId]: { ...state.manualEdgeStages?.[sourceId], [werkKey]: {} } } };
    }
    case "SET_DEPLOY_STAGES": {
      const { edge, values, reset } = action.payload || {};
      if (isPackageTransport(state) && !edge) return state;
      const allowed = vorwaertsAlleZiele(state).map(stage => stage.key);
      if (!values || Object.entries(values).some(([key, value]) => !allowed.includes(key) || typeof value !== "boolean")) return state;
      const field = edge ? "manualEdgeStages" : "deployStages", next = { ...state[field] };
      for (const [id, plants] of Object.entries(state.sel || {})) for (const b of buckets(state)) {
        const key = plants[b.key] ? b.key : b.kennung;
        if (key && plants[key] && !selectedArtifactIsDraft(state, id, key))
          next[id] = { ...next[id], [key]: reset ? {} : { ...next[id]?.[key], ...values } };
      }
      return { ...state, [field]: next };
    }
    case "SET_PACKAGE_DEPLOY": {
      if (!isPackageTransport(state) || packageDeployLocked(state) || typeof action.payload !== "boolean") return state;
      return { ...state, packageDeploy: action.payload, packageDeployStages: {}, packagePrepared: false, configReviewed: false };
    }
    case "SET_PACKAGE_DEPLOY_STAGES": {
      if (!isPackageTransport(state) || packageDeployLocked(state)) return state;
      const { values, reset } = action.payload || {};
      if (reset) return { ...state, packageDeployStages: {}, packagePrepared: false, configReviewed: false };
      const allowed = vorwaertsAlleZiele(state).map(stage => stage.key);
      if (!values || Object.entries(values).some(([key, value]) => !allowed.includes(key) || typeof value !== "boolean")) return state;
      return { ...state, packageDeployStages: { ...state.packageDeployStages, ...values }, packagePrepared: false, configReviewed: false };
    }
    case "SET_DESIGN_EDGE_DEPLOY": {
      const { designId, on } = action.payload || {};
      if (typeof on !== "boolean" || typeof designId !== "string") return state;
      return { ...state, designEdgeDeploy: { ...state.designEdgeDeploy, [designId]: on }, packagePrepared: false, configReviewed: false };
    }
    case "SET_EXTENSION_VERSION": {
      const version = typeof action.payload === "number" && Number.isFinite(action.payload) ? action.payload : null;
      return { ...state, extensionVersion: version };
    }
    case "SET_REF_DEPLOY": {
      // Pakettransport: keine Referenz wählbar, auch nicht ihr Deployment (siehe TOGGLE_REF).
      if (isPackageTransport(state)) return state;
      const { id, on } = action.payload || {};
      const ref = (state.globalRefs || []).find(r => refKey(r) === id);
      if (!state.refSel?.[id] || !ref || typeof on !== "boolean" || (!on && referenceDeployRequired(state, ref))) return state;
      return { ...state, refDeploy: { ...state.refDeploy, [id]: on } };
    }
    case "TOGGLE_REF": {
      // Pakettransport: eigene Referenzen reisen im SAP-Export mit, fremde werden nur geprüft. Nichts ist wählbar.
      if (isPackageTransport(state)) return state;
      const id = action.payload;
      // Nicht-aufloesbare Referenz (resolvable=false) darf NIE in die Auswahl geraten. Die Checkbox
      // ist zwar disabled, aber ein programmatischer Dispatch soll ebenfalls abprallen (Defense-in-depth).
      const ref = (state.globalRefs || []).find((r) => (r?.bundleId || r?.id || r?.Id || r?.name || r?.Name) === id);
      if (!ref || ref.resolvable === false) return state;
      if (!state.refSel[id] && needsSameReferenceVersionPermission(ref) && !state.canTransportSameVersionReferences) return state;
      // Entwuerfe prallen genauso ab wie beim Vorbelegen (SET_GLOBAL_REFS): ohne aktive
      // Version kann der Snapshot sie nicht laden und der Transport braeche ab.
      const refDraft = ref?.draft === true || String(ref?.status || "").toLowerCase() === "draft" || String(ref?.version || ref?.Version || "").toLowerCase() === "draft";
      if (refDraft) return state;
      // Nicht transportierbare Referenz (transportierbar=false) prallt genauso ab: sie wird nur
      // gemeldet, ein Haken haette keinen Transport zur Folge und wuerde den Snapshot verwirren.
      if (refNichtTransportierbar(ref)) return state;
      // Referenzen folgen den iFlows: ohne einen nutzenden iFlow im Transport laesst sich eine
      // Referenz nicht anwaehlen (die Checkbox ist gesperrt, der Dispatch prallt ebenfalls ab).
      if (refNutzer(ref).length && !refGebraucht(ref, gewaehlteIflows(state.sel))) return state;
      return { ...state, refSel: { ...state.refSel, [id]: !state.refSel[id] } };
    }
    case "TOGGLE_EXPAND": {
      const id = action.payload;
      return { ...state, expanded: { ...state.expanded, [id]: !state.expanded[id] } };
    }
    case "SET_EXPANDED":
      return { ...state, expanded: action.payload || {} };
    case "SET_WORKING_TAB":
      return { ...state, workingTab: action.payload, workingTabAuto: false };
    case "SET_WORKING_TAB_AUTO":
      return { ...state, workingTab: action.payload, workingTabAuto: true };
    case "SET_CFG": {
      // stageValues (optional, Etappe B): { [stageKey]: [{ParameterKey, targetValue}] }.
      // Wird pro Eintrag in entries[i].byStage[stageKey] zusammengefuehrt. Der Root des
      // Eintrags bleibt die BASIS (erste Vorwaerts-Stufe) - Rueckwaerts-kompatibel zur
      // Ein-Stufen-Ansicht.
      const { werkKey, sourceId, cacheKey, entries, stageValues } = action.payload;
      const stufen = stageValues && typeof stageValues === "object" ? stageValues : null;
      // Pro Stufe ein Lookup ParameterKey -> targetValue (fuer schnellen Zugriff im map).
      const stageLookup = {};
      if (stufen) {
        for (const [stageKey, list] of Object.entries(stufen)) {
          const m = {};
          for (const v of Array.isArray(list) ? list : []) {
            if (v && v.ParameterKey != null) m[v.ParameterKey] = v.targetValue;
          }
          stageLookup[stageKey] = m;
        }
      }
      // Values this plant has maintained in Git. They BEAT the tenant's current value: Git is the
      // truth for the parameters, the tenant only shows what happens to be there. Without that
      // precedence the user would see the tenant value on every transport, adopt it, and the
      // state maintained in Git would be silently overwritten.
      const gitWerk = (state.gitCfg && state.gitCfg[werkKey]) || {};
      const gitArt = gitWerk[sourceId] || {};
      const enrichedEntries = (entries || []).map((e) => {
        if (!e) return e;
        const git = gitArt[e.ParameterKey];
        // B1/Import: Git-Basis = Wert der ersten Vorwärtsstufe (z. B. QAS), dann Default.
        // Damit zeigt ein importierter {Default: DEV, QAS: Y} den Wert Y.
        const ersteVorwaerts = state.space?.zielStufe;
        const gitBasis = (ersteVorwaerts && git?.values?.[ersteVorwaerts]) ?? git?.values?.__base__;
        // fromTarget has to be reset as well: if the Git value happens to match the tenant value,
        // the row would otherwise read "from target" although the value comes from Git. byStage
        // below already handles this correctly.
        // gitStale: Quelle nicht konfiguriert, Git hat einen Basiswert — der Git-Wert
        // wird vorbelegt und die Zeile gekennzeichnet. Geleert wird NUR durch ausdrückliches
        // Leeren des Benutzers (A2 → __base__:"").
        const quellLeer = !e.defaultValue && !e.ParameterValue;
        const root = gitBasis != null && gitBasis !== ""
          ? { ...e, newValue: gitBasis, fromGit: true, fromTarget: false,
              ...(quellLeer ? { gitStale: true } : {}) }
          : e;
        if (!stufen) return root;
        const byStage = { ...(root.byStage || {}) };
        for (const stageKey of Object.keys(stageLookup)) {
          const targetValue = stageLookup[stageKey][e.ParameterKey];
          const gitWert = git?.values?.[stageKey];
          const ausGit = gitWert != null && gitWert !== "";
          // D2: Vorbelegung späterer Stufen aus Git-Stufenschlüssel. Der Zieltenant-Wert ist
          // nur Hinweis — AUSSER die Basis ist leer: dann wird der Tenantwert vorbelegt, damit
          // er als eigener Stufenschlüssel nach Git geht und nicht durch Default:"" gelöscht wird.
          const basisLeer = !root.newValue;
          const ausTarget = !ausGit && basisLeer && !!targetValue;
          byStage[stageKey] = {
            targetValue: targetValue == null ? "" : targetValue,
            newValue: ausGit ? gitWert : (ausTarget ? targetValue : ""),
            fromTarget: ausTarget,
            fromGit: ausGit,
            modified: false,
          };
        }
        return { ...root, byStage };
      // Bekanntes Auswahlfeld (Backend: adapterField, z. B. proxyType): Bezeichnungen aus Git oder
      // Ziel-Tenant („Internet“) werden VOR Anzeige und Versand auf den SAP-Schlüssel (default)
      // umgesetzt; der Eintrag bekommt mappedFrom für den Hinweis. Unbekannte Werte bleiben stehen.
      }).map(normalizeEntryAdapterValues);
      // A1: Getippte Werte (modified) IMMER behalten, nicht nur im Mehrzielmodus. Ein
      // verspätetes SET_CFG (z. B. doppelt gestartetes loadFor) darf keine Eingaben verwerfen.
      const previous = state.cfg[werkKey]?.[sourceId]?.entries ?? null;
      const keptEntries = previous ? enrichedEntries.map(entry => {
        const old = entry && previous.find(item => item.ParameterKey === entry.ParameterKey);
        if (!old) return entry;
        const root = old.modified ? { ...entry, newValue: old.newValue, modified: true, fromTarget: false, fromGit: false } : entry;
        const byStage = { ...(root.byStage || {}) };
        for (const [key, value] of Object.entries(old.byStage || {})) if (value?.modified) byStage[key] = value;
        return { ...root, byStage };
      }) : enrichedEntries;
      const werkCfg = { ...(state.cfg[werkKey] || {}) };
      werkCfg[sourceId] = { cacheKey, entries: keptEntries };
      return { ...state, cfg: { ...state.cfg, [werkKey]: werkCfg }, cfgInvalidated: false,
        cfgSuggestionUndo: state.cfgSuggestionUndo?.filter(change => change.werkKey !== werkKey || change.sourceId !== sourceId) ?? null };
    }
    case "SET_GIT_CFG": {
      // Preloaded Git values. The key is the UI's plant key ("std" = no plant), while the backend
      // sends the kennung ("" = no plant) - rekeyed once here so the rest of the view only ever
      // deals with one spelling.
      const { werke, unvollstaendig, scopeKey } = action.payload || {};
      const gitCfg = {};
      for (const [kennung, artefakte] of Object.entries(werke || {})) {
        gitCfg[kennung === "" ? "std" : kennung] = artefakte || {};
      }
      return { ...state, gitCfg, gitCfgScope: scopeKey ?? null, gitCfgState: "da", gitCfgUnvollstaendig: !!unvollstaendig };
    }
    case "SET_GIT_CFG_STATE":
      return { ...state, gitCfgState: action.payload };
    case "TOGGLE_CFG_SUGGESTIONS":
      return toggleConfigSuggestions(state, buckets(state));
    case "PATCH_CFG_ENTRY": {
      // Optional payload.stage: schreibt in entries[i].byStage[stage], sonst in den Root (Basis).
      const { werkKey, sourceId, paramKey, patch, stage } = action.payload;
      const werk = state.cfg[werkKey] || {};
      const artef = werk[sourceId] || { entries: [] };
      const entries = (artef.entries || []).map((e) => {
        if (e.ParameterKey !== paramKey) return e;
        if (!stage) return { ...e, ...patch };
        const byStage = { ...(e.byStage || {}) };
        byStage[stage] = { ...(byStage[stage] || {}), ...patch };
        return { ...e, byStage };
      });
      const nextArtef = { ...artef, entries };
      return {
        ...state,
        cfgSuggestionUndo: !stage && state.cfgSuggestionUndo
          ? state.cfgSuggestionUndo.filter(change => change.werkKey !== werkKey || change.sourceId !== sourceId || change.paramKey !== paramKey)
          : state.cfgSuggestionUndo,
        cfg: {
          ...state.cfg,
          [werkKey]: { ...werk, [sourceId]: nextArtef },
        },
      };
    }
    case "SET_CFG_TAB":
      return { ...state, cfgTab: action.payload };
    case "SET_SCHEDULE_COUNT":
      return { ...state, scheduleCount: action.payload };
    case "SET_PIPELINE_STATE":
      return {
        ...state,
        pipeline: action.payload.pipeline,
        pipelineError: action.payload.error ?? state.pipelineError,
        runInfo: action.payload.runInfo ?? state.runInfo,
      };
    case "APPEND_LOG":
      return { ...state, execLogs: [...state.execLogs, action.payload] };
    case "RESET_LOGS":
      return { ...state, execLogs: [] };
    case "SET_PD_REPLICATE": {
      // Abwaehlen leert die Auswahl (nichts replizieren), statt den Bereich zu verstecken - die
      // Liste bleibt sichtbar und bedienbar, wie die Kopf-Checkboxen der vorherigen Schritte.
      // Z1: pdPrefillKey zuruecksetzen, damit ein erneutes Anwaehlen die Vorbelegung wiederholt.
      if (!action.payload) return { ...state, pdReplicate: false, pdSelectedPids: [], pdStagesByPid: {}, pdPrefillKey: null };
      return { ...state, pdReplicate: true };
    }
    case "SET_PD_EDGE_MODE": {
      const { pid, mode, targets = {} } = action.payload || {};
      if (state.batch || state.batchRequest || state.pdEdgeDefaultsLocked || (state.pipeline && state.pipeline !== "idle")
        || !(state.pdSelectedPids || []).includes(pid) || !["cloud", "edge"].includes(mode)) return state;
      const allowed = pdStageKeys(state);
      const active = state.pdStagesByPid?.[pid] || allowed;
      const nextTargets = {}, choices = {};
      for (const stage of allowed) {
        if (mode === "cloud") choices[stage] = null;
        else if (active.includes(stage) && typeof targets[stage] === "string" && targets[stage].trim()) {
          nextTargets[stage] = targets[stage]; choices[stage] = targets[stage];
        }
      }
      return { ...state, pdEdgeModeByPid: { ...state.pdEdgeModeByPid, [pid]: mode },
        pdEdgeByPid: { ...state.pdEdgeByPid, [pid]: nextTargets },
        pdEdgeChoicesByPid: { ...state.pdEdgeChoicesByPid, [pid]: choices } };
    }
    case "SET_PD_EDGE": {
      const { pid, stage, runtimeId } = action.payload || {};
      if (!(state.pdSelectedPids || []).includes(pid) || !pdStageKeys(state).includes(stage)
        || !(state.pdStagesByPid?.[pid] || pdStageKeys(state)).includes(stage)
        || (runtimeId !== null && (typeof runtimeId !== "string" || !runtimeId.trim()))) return state;
      const stages = { ...state.pdEdgeByPid?.[pid] };
      if (runtimeId === null) delete stages[stage]; else stages[stage] = runtimeId;
      return { ...state, pdEdgeByPid: { ...state.pdEdgeByPid, [pid]: stages },
        pdEdgeChoicesByPid: { ...state.pdEdgeChoicesByPid,
          [pid]: { ...state.pdEdgeChoicesByPid?.[pid], [stage]: runtimeId } } };
    }
    case "SET_PD_GIT_PACKAGE_PARTNERS": {
      // Z2/Z4/Z5: Git-zugeordnete PIDs und Edge-Laufzeiten aus package-partners. Nur beim aktuellen Paket anwenden.
      const data = action.payload;
      if (!data || data.available !== true || !Array.isArray(data.entries)) return { ...state, pdGitPackagePartners: data || null, pdGitEdgeTargets: {} };
      const gitEdge = {};
      for (const entry of data.entries) {
        if (!entry.pid || !entry.edgeTargets || typeof entry.edgeTargets !== "object" || Array.isArray(entry.edgeTargets)) continue;
        const targets = {};
        for (const [stage, runtime] of Object.entries(entry.edgeTargets)) {
          if (typeof runtime === "string" && runtime.trim()) targets[stage] = runtime;
        }
        if (Object.keys(targets).length) gitEdge[entry.pid] = targets;
      }
      return { ...state, pdGitPackagePartners: data, pdGitEdgeTargets: gitEdge };
    }
    case "SET_PD_SUMMARY":
      return { ...state, pdSummary: action.payload || { pids: 0, chained: 0 } };
    case "SET_PD_SELECTED_PIDS": {
      const list = Array.isArray(action.payload) ? action.payload : [];
      const dedup = Array.from(new Set(list.filter((p) => typeof p === "string" && p)));
      // Neue Pids mit allen Vorwaerts-Stufen vorbelegen; bereits vorhandene bleiben.
      // So verhaelt sich ein Pid ohne bewusste Einschraenkung genau wie heute: alle Stufen.
      const defaults = pdStageKeys(state);
      const stagesNext = pdStagesVorbelegen(state.pdStagesByPid || {}, dedup, defaults);
      // Aufraeumen: Stufen-Eintraege abgewaehlter Pids fallen mit raus. Sonst wuerde ein
      // Abwaehlen + spaeteres Wiederauswaehlen den alten (moeglicherweise reduzierten)
      // Filter zurueckholen, den der Nutzer nicht mehr im Kopf hat.
      const selSet = new Set(dedup);
      const cleaned = {};
      for (const [pid, stg] of Object.entries(stagesNext || {})) {
        if (selSet.has(pid)) cleaned[pid] = stg;
      }
      // Das Flag folgt der Auswahl: eine Pid gewaehlt heisst replizieren, keine heisst nichts.
      return { ...state, pdSelectedPids: dedup, pdStagesByPid: cleaned, pdReplicate: dedup.length > 0 };
    }
    case "TOGGLE_PD_PID": {
      const pid = action.payload;
      if (!pid || typeof pid !== "string") return state;
      const cur = new Set(state.pdSelectedPids || []);
      const stagesNext = { ...(state.pdStagesByPid || {}) };
      if (cur.has(pid)) {
        cur.delete(pid);
        // Filter mit raus, damit ein spaeteres Wieder-Anwaehlen sauber vorbelegt startet.
        delete stagesNext[pid];
      } else {
        cur.add(pid);
        // Vorbelegung nur setzen, wenn noch kein Eintrag existiert.
        if (!Array.isArray(stagesNext[pid])) {
          const defaults = pdStageKeys(state);
          if (defaults.length) stagesNext[pid] = [...defaults];
        }
      }
      return { ...state, pdSelectedPids: Array.from(cur), pdStagesByPid: stagesNext, pdReplicate: cur.size > 0 };
    }
    case "TOGGLE_PD_STAGE": {
      // Einzelne Stufe fuer eine Pid an/aus. Wird beim Abwaehlen der letzten Stufe
      // der Pid komplett aus der Auswahl entfernt - sonst bliebe ein "Pid ausgewaehlt,
      // aber fuer keine Stufe" widerspruechlich in der Oberflaeche stehen.
      const { pid, stage } = action.payload || {};
      if (!pid || !stage) return state;
      const cur = new Set(state.pdStagesByPid?.[pid] || pdStageKeys(state));
      if (cur.has(stage)) cur.delete(stage); else cur.add(stage);
      const naechsteStufen = Array.from(cur);
      if (naechsteStufen.length === 0) {
        // Letzte Stufe entfernt -> Pid faellt raus. Konsistent zum Verhalten
        // "keine Stufe = nirgends transportieren".
        const selNext = (state.pdSelectedPids || []).filter((p) => p !== pid);
        const stagesNext = { ...(state.pdStagesByPid || {}) };
        delete stagesNext[pid];
        return { ...state, pdSelectedPids: selNext, pdStagesByPid: stagesNext, pdReplicate: selNext.length > 0 };
      }
      return {
        ...state,
        pdStagesByPid: { ...(state.pdStagesByPid || {}), [pid]: naechsteStufen },
      };
    }
    case "SET_PD_STAGES": {
      // Kompletten Stufen-Satz fuer eine Pid setzen (z.B. "alle an" / "alle aus").
      // Leere Liste -> Pid faellt raus (siehe TOGGLE_PD_STAGE).
      const { pid, stages } = action.payload || {};
      if (!pid) return state;
      const bereinigt = Array.from(new Set((Array.isArray(stages) ? stages : []).filter(Boolean)));
      if (bereinigt.length === 0) {
        const selNext = (state.pdSelectedPids || []).filter((p) => p !== pid);
        const stagesNext = { ...(state.pdStagesByPid || {}) };
        delete stagesNext[pid];
        return { ...state, pdSelectedPids: selNext, pdStagesByPid: stagesNext, pdReplicate: selNext.length > 0 };
      }
      return {
        ...state,
        pdStagesByPid: { ...(state.pdStagesByPid || {}), [pid]: bereinigt },
      };
    }
    case "SET_RELEASE_STATE":
      return {
        ...state,
        releaseState: action.payload.state,
        releaseError: action.payload.error ?? state.releaseError,
      };
    case "SET_RELEASE_CANDIDATE":
      return { ...state, releaseCandidate: action.payload };
    case "SET_FINALIZED":
      return { ...state, finalized: !!action.payload };
    case "RESET_ALL":
      return { ...initialState, canTransportSameVersion: state.canTransportSameVersion === true,
        canTransportSameVersionReferences: state.canTransportSameVersionReferences === true, scheduleCount: state.scheduleCount };
    default:
      return state;
  }
}

export function extractWerkeFromInstance(inst) {
  if (!inst || !Array.isArray(inst.werke)) return [];
  return inst.werke
    .map((w) => {
      if (typeof w === "string") {
        const parts = w.split("__");
        if (parts.length >= 2) return { key: parts[1] || parts[0], name: parts[0], kennung: parts[1] };
        return { key: parts[0], name: parts[0], kennung: parts[0] };
      }
      const key = w?.kennung || w?.name || "";
      return { key, name: w?.name || key, kennung: w?.kennung || key };
    })
    .filter((w) => w.kennung);
}

const Ctx = createContext(null);

export function TransportV2Provider({ children, user }) {
  const permission = canTransportSameVersion(user);
  const referencePermission = canTransportSameVersionReferences(user);
  const [state, rawDispatch] = useReducer(reducer, { ...initialState, canTransportSameVersion: permission, canTransportSameVersionReferences: referencePermission });
  useLayoutEffect(() => { rawDispatch({ type: "SET_SAME_VERSION_PERMISSION", payload: permission }); }, [permission]);
  useLayoutEffect(() => { rawDispatch({ type: "SET_SAME_REFERENCE_VERSION_PERMISSION", payload: referencePermission }); }, [referencePermission]);
  const dispatch = useMemo(() => (action) => {
    const navigation = ["TOGGLE_PACKAGE", "ACTIVATE_PACKAGE", "PICK_SPACE", "PICK_PACKAGE", "TOGGLE_WERK", "SET_ALL_WERKE", "RESET_ALL", "SET_STEP"].includes(action.type);
    rawDispatch(navigation || action.type.startsWith("PACKAGE_PRECHECK_") ? action : { ...action, scope: { pkgId: state.pkgId, epoch: state.packageEpoch } });
  }, [state.pkgId, state.packageEpoch]);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTransportV2() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useTransportV2 muss innerhalb TransportV2Provider gerufen werden");
  return ctx;
}

// Selektoren
export function hasWerke(state) {
  return !!(state.space?.werkeList?.length);
}
export function allWerke(state) {
  return state.space?.werkeList || [];
}
// Schritt 1 (Landschaften) hat nur mit gepflegten Werken einen eigenen Inhalt (Werke zuschalten);
// ohne Werke zeigt er nur, was die Space-Karte in Schritt 0 schon sagt.
export function landschaftsSchrittNoetig(state) {
  return allWerke(state).length > 0 || (state.multiTarget === true && Object.values(state.stageTargets || {}).some(rows => rows.length > 1));
}
export function activeWerke(state) {
  return allWerke(state).filter((w) => state.werkOn?.[w.key]);
}
// Configured plants stay available in the landscape; only selected plants change the transport.
export function usesWerke(state) {
  return activeWerke(state).length > 0;
}
export function buckets(state) {
  return usesWerke(state) ? activeWerke(state) : [{ key: "std", name: "Standard", kennung: "" }];
}

// Pakettransport ab v27: CI ist paketweite Entscheidung. Alte Repos (< v27): fest an.
export const PACKAGE_DEPLOY_MIN_EXTENSION = 27;
export function packageDeployLocked(state) {
  return typeof state.extensionVersion === "number" && state.extensionVersion < PACKAGE_DEPLOY_MIN_EXTENSION;
}
export function packageDeployValue(state) {
  if (packageDeployLocked(state)) return true;
  return state.packageDeploy === true;
}
export function packageStageDeployValue(state, stage) {
  if (packageDeployLocked(state)) return true;
  const override = state.packageDeployStages?.[stage];
  return typeof override === "boolean" ? override : packageDeployValue(state);
}
export function hasPackageStageDeployment(state) {
  if (packageDeployLocked(state)) return false;
  return vorwaertsAlleZiele(state).some(stage => typeof state.packageDeployStages?.[stage.key] === "boolean");
}
export function designEdgeRequested(state, designId) {
  return state.designEdgeDeploy?.[designId] === true;
}
export function autoDeploy(state, sourceId, werkKey) {
  if (!state.sel?.[sourceId]?.[werkKey]) return false;
  if (isPackageTransport(state)) return packageDeployValue(state);
  return state.deploy?.[sourceId]?.[werkKey] !== false;
}
function selectedArtifactIsDraft(state, sourceId, werkKey) {
  return Object.values(state.precheckByBucket || {}).some(entry => (entry.werk?.kennung || "std") === werkKey
    && (entry.comparison || []).some(artifact => (artifact.originalId || artifact.Id) === sourceId && isArtifactDraft(artifact)));
}
export function manualEdgeRequested(state, sourceId, werkKey) {
  if (!state.sel?.[sourceId]?.[werkKey]) return false;
  // Pakettransport ab v27: Edge je iFlow frei wählbar (Standard aus).
  return state.manualEdgeDeploy?.[sourceId]?.[werkKey] === true;
}
export function stageDeployment(state, sourceId, werkKey, stage, edge = false) {
  if (isPackageTransport(state)) {
    if (edge) {
      // Edge je iFlow je Stufe: wie im Artefaktweg, aus manualEdgeStages oder Basiswert.
      const override = state.manualEdgeStages?.[sourceId]?.[werkKey]?.[stage];
      return typeof override === "boolean" ? override : manualEdgeRequested(state, sourceId, werkKey);
    }
    return packageStageDeployValue(state, stage);
  }
  const field = edge ? "manualEdgeStages" : "deployStages";
  const override = state[field]?.[sourceId]?.[werkKey]?.[stage];
  return typeof override === "boolean" ? override : edge ? manualEdgeRequested(state, sourceId, werkKey) : autoDeploy(state, sourceId, werkKey);
}
export function hasStageDeployment(state, sourceId, werkKey, edge = false) {
  if (isPackageTransport(state)) {
    // CI: Stufenabweichung ist paketglobal, nicht je Zeile.
    if (!edge) return hasPackageStageDeployment(state);
    // Edge je iFlow: Stufenabweichung je Zeile (wie im Artefaktweg).
    return vorwaertsAlleZiele(state).some(stage => typeof state.manualEdgeStages?.[sourceId]?.[werkKey]?.[stage.key] === "boolean");
  }
  const field = edge ? "manualEdgeStages" : "deployStages";
  return vorwaertsAlleZiele(state).some(stage => typeof state[field]?.[sourceId]?.[werkKey]?.[stage.key] === "boolean");
}
export function deploymentSelection(state) {
  const artifacts = {}, manualEdgeArtifacts = {};
  const stages = Object.fromEntries(vorwaertsAlleZiele(state).map(stage => [stage.key, { artifacts: {}, manualEdgeArtifacts: {} }]));
  for (const [id, plants] of Object.entries(state.sel || {})) for (const b of buckets(state)) {
    const selectionKey = plants[b.key] ? b.key : b.kennung;
    if (selectionKey && plants[selectionKey] && !selectedArtifactIsDraft(state, id, selectionKey)) {
      if (!artifacts[id]) artifacts[id] = {};
      if (!manualEdgeArtifacts[id]) manualEdgeArtifacts[id] = {};
      artifacts[id][b.kennung || b.key] = autoDeploy(state, id, selectionKey);
      manualEdgeArtifacts[id][b.kennung || b.key] = manualEdgeRequested(state, id, selectionKey);
      for (const [stage, plan] of Object.entries(stages)) {
        if (!plan.artifacts[id]) plan.artifacts[id] = {};
        if (!plan.manualEdgeArtifacts[id]) plan.manualEdgeArtifacts[id] = {};
        plan.artifacts[id][b.kennung || b.key] = stageDeployment(state, id, selectionKey, stage);
        plan.manualEdgeArtifacts[id][b.kennung || b.key] = stageDeployment(state, id, selectionKey, stage, true);
      }
    }
  }
  const result = { artifacts, manualEdgeArtifacts, ...(Object.keys(stages).length ? { stages } : {}) };
  // Pakettransport (v27): Paketweite CI Entscheidung und Edge je deploybarem Designobjekt.
  if (isPackageTransport(state)) {
    result.package = {
      deploy: packageDeployValue(state),
      stages: Object.fromEntries(vorwaertsAlleZiele(state).map(stage => [stage.key, packageStageDeployValue(state, stage.key)])),
    };
    const deployable = packageDesignObjects(state).filter(obj => obj.deployable);
    // Das Backend prüft die SAP-Schreibweise exakt; deploybar erkennt packageTransport.js ohne Groß/Klein und Leerzeichen.
    const canonical = { messagemapping: "MessageMapping", scriptcollection: "ScriptCollection", valuemapping: "ValueMapping" };
    if (deployable.length) {
      result.designArtifacts = Object.fromEntries(deployable.map(obj => [
        obj.id, { type: canonical[String(obj.rawType || obj.type || "").replace(/\s+/g, "").toLowerCase()] || obj.rawType || obj.type,
          manualEdgeDeployment: designEdgeRequested(state, obj.id) },
      ]));
    }
  }
  return result;
}
export function referenceDeployRequired(state, ref) {
  const consumers = refNutzer(ref);
  const selection = deploymentSelection(state);
  const plans = selection.stages ? Object.values(selection.stages) : [selection];
  return plans.some(plan => Object.entries(plan.artifacts).some(([id, plants]) => (!consumers.length || consumers.includes(id)) && Object.values(plants).some(Boolean)));
}
export function referenceAutoDeploy(state, ref) {
  return referenceDeployRequired(state, ref) || state.refDeploy?.[refKey(ref)] === true;
}
export function visBuckets(state) {
  const b = buckets(state);
  if (usesWerke(state) && state.workingTab !== "all") {
    return b.filter((x) => x.key === state.workingTab);
  }
  return b;
}

// Vorwaerts-Stufen aus Scene0: alle Stufen ab der Zielstufe (inklusiv), nur die mit
// erreichbarer Instanz. Beispiel: Space DEV/QAS/PRE-PROD/PROD, Zielstufe QAS -> [QAS, PRE-PROD, PROD].
// Ein-Stufen-Space (nur Quelle + Ziel) -> [QAS] (Rueckwaerts-kompatibel zur Ein-Stufen-Ansicht).
// Jede Stufe: { key, code, name, instanceId }.
export function vorwaertsStufen(state) {
  const s = state.space;
  if (!s || !Array.isArray(s.stages)) return [];
  const zielKey = s.zielStufe || null;
  if (!zielKey) return [];
  const startIdx = s.stages.findIndex((st) => st.stageKey === zielKey);
  if (startIdx < 0) return [];
  const out = [];
  for (let i = startIdx; i < s.stages.length; i++) {
    const st = s.stages[i];
    if (!st || !(st.instanceId || st.candidates?.length)) continue;
    out.push({ key: st.stageKey, code: st.code, name: st.inst || st.name, instanceId: st.instanceId });
  }
  return out;
}

// Spalten der gewählten Ziele (Vertrag V.1): ohne Mehrzielmodus identisch zu vorwaertsStufen(state), sonst je Ziel
// `{ key: targetKey, stageKey, targetId, code, name, instanceId, region, isDefault }`.
export function vorwaertsAlleZiele(state) {
  const stages = vorwaertsStufen(state);
  return state.multiTarget ? allTargetColumns(stages, state.stageTargets) : stages;
}
// Stufen mit Zielen im Format der Erbkette (Vertrag V.11).
export function vorwaertsZielKette(state) {
  return forwardTargets(vorwaertsStufen(state), state.multiTarget ? state.stageTargets : null);
}
export function vorwaertsZiele(state) {
  const stages = vorwaertsStufen(state);
  return state.multiTarget ? targetColumns(stages, state.stageTargets, state.stageTargetSel) : stages;
}

// Selektor: Effektive PD-Pids je Ziel-Stufe. Fehlt der Filter fuer eine Pid,
// gilt (wie im Backend-Export) implizit "alle Stufen". Damit ohne bewusste
// Einschraenkung genau das Altverhalten laeuft. targetStage: normalisierter
// Stufen-Key ("qas"/"preprod"/"prod"/"dev").
export function pdPidsFuerStufe(state, targetStage) {
  const list = Array.isArray(state.pdSelectedPids) ? state.pdSelectedPids : [];
  if (!targetStage) return list.slice();
  const map = state.pdStagesByPid || {};
  return list.filter((pid) => {
    const stg = map[pid];
    if (!Array.isArray(stg) || stg.length === 0) return true; // implizit alle Stufen
    // Im Mehrzielmodus stehen Zielschlüssel (stage@targetId) in der Liste; eine Stufe gilt, sobald eines ihrer Ziele gewählt ist.
    return stg.includes(targetStage) || stg.some(key => typeof key === "string" && key.startsWith(`${targetStage}@`));
  });
}

// Keep only explicitly selected PIDs and stages. Cloud is the absence of an Edge target.
export function pdEdgeSelection(state) {
  if (!state.pdReplicate) return {};
  const result = {};
  const allowed = pdStageKeys(state);
  for (const pid of state.pdSelectedPids || []) {
    const stages = state.pdStagesByPid?.[pid] || allowed;
    const targets = Object.fromEntries(Object.entries(state.pdEdgeByPid?.[pid] || {}).filter(([stage, runtime]) =>
      stages.includes(stage) && allowed.includes(stage) && typeof runtime === "string" && runtime.trim()));
    if (Object.keys(targets).length) result[pid] = targets;
  }
  return result;
}

// Capabilities are authoritative; never infer readiness from instance metadata.
// Several runtimes require the existing explicit selector, not the first entry.
export function pdEdgeMode(state, pid) {
  const explicit = state.pdEdgeModeByPid?.[pid];
  if (explicit === "cloud" || explicit === "edge") return explicit;
  const stages = (state.pdStagesByPid?.[pid] || pdStageKeys(state)).filter(stage => !state.multiTarget || pdEdgeKeys(state).includes(stage));
  const chosen = stages.filter(stage => state.pdEdgeByPid?.[pid]?.[stage]);
  if (chosen.length && chosen.length !== stages.length) return "mixed";
  return chosen.length ? "edge" : "cloud";
}

export function pdEdgeConflictPids(state) {
  if ((state.selectedPackages || []).length < 2) return [];
  const choices = new Map(), conflicts = new Set();
  for (const draft of packageStates(state)) for (const pid of draft.pdReplicate ? draft.pdSelectedPids || [] : []) {
    const mode = pdEdgeMode(draft, pid);
    const previous = choices.get(pid);
    if (previous && previous.mode !== mode) conflicts.add(pid);
    const targets = draft.pdEdgeByPid?.[pid] || {};
    if (previous && Object.entries(targets).some(([stage, runtime]) => previous.targets[stage] && previous.targets[stage] !== runtime)) conflicts.add(pid);
    choices.set(pid, { mode, targets: { ...previous?.targets, ...targets } });
  }
  return [...conflicts];
}

// Only a deliberate Edge choice or an existing runtime assignment requests Edge.
// New partners stay on the Cloud option shown by the mode control.
export function pdEdgeRequested(state, pid) {
  if (state.pdEdgeModeByPid?.[pid] === "cloud") return false;
  return state.pdEdgeModeByPid?.[pid] === "edge"
    || Object.values(state.pdEdgeByPid?.[pid] || {}).some(Boolean)
    || Object.values(state.pdEdgeChoicesByPid?.[pid] || {}).some(value => typeof value === "string" && value.trim());
}

export function withPdEdgeDefaults(state, capabilities = {}) {
  if (!state.pdReplicate || state.pdEdgeDefaultsLocked || state.batch || state.batchRequest
    || (state.pipeline && state.pipeline !== "idle")) return state;
  const original = state.pdEdgeByPid || {};
  let edge = original;
  const allowed = pdEdgeKeys(state);
  for (const pid of state.pdSelectedPids || []) {
    // Z3: Kein pdEdgeRequested-Guard mehr. Jede ausgewaehlte PID bekommt den Default
    // "Cloud + Edge", wenn jede Stufe genau eine Runtime hat. Bewusstes Cloud bleibt erhalten.
    const stages = (state.pdStagesByPid?.[pid] || allowed).filter(stage => allowed.includes(stage));
    const choices = state.pdEdgeChoicesByPid?.[pid] || {};
    // Explicit runtime choices remain visible even if the runtime was removed meanwhile.
    if (state.pdEdgeModeByPid?.[pid] !== "cloud") for (const stage of stages) {
      const remembered = choices[stage];
      if (!edge[pid]?.[stage] && typeof remembered === "string" && remembered.trim())
        edge = { ...edge, [pid]: { ...edge[pid], [stage]: remembered } };
    }
    if (state.pdEdgeModeByPid?.[pid] === "cloud" || pdEdgeMode(state, pid) === "mixed"
      || stages.some(stage => choices[stage] === null)) continue;
    // A default is one PID-wide decision. Never silently create a partial Edge plan.
    const previousTargets = edge[pid] || {};
    const gitTargets = state.pdGitEdgeTargets?.[pid] || {};
    const targets = stages.map(stage => {
      const target = pdKeyInstance(state, stage);
      const info = capabilities[target?.instanceId];
      if (previousTargets[stage]) return [stage, previousTargets[stage]];
      if (info?.available !== true || info.loading || info.error) return null;
      // Z4: Git-zugeordnete Edge-Laufzeit gewinnt, wenn die Runtime noch existiert.
      // Nicht mehr vorhanden: ausdrückliche Wahl verlangen, nie still umstellen.
      // Defensiv: Nicht-String-Werte (z. B. Objekte aus zukuenftigem Format) ignorieren.
      const gitRuntime = gitTargets[stage];
      if (typeof gitRuntime === "string" && gitRuntime.trim()) {
        if (info.targets?.some(t => t.id === gitRuntime)) return [stage, gitRuntime];
        return null;
      }
      const remembered = choices[stage];
      const runtime = remembered ? remembered
        : info.targets?.length === 1 ? info.targets[0]?.id : null;
      return typeof runtime === "string" && runtime.trim() ? [stage, runtime] : null;
    });
    if (!targets.length || targets.some(target => !target)) continue;
    const next = Object.fromEntries(targets);
    if (targets.every(([stage, runtime]) => previousTargets[stage] === runtime)) continue;
    edge = { ...edge, [pid]: next };
  }
  return edge === original ? state : { ...state, pdEdgeByPid: edge, packagePrepared: false };
}

export function bulkDeploymentChoices(state) {
  const drafts = state.selectedPackages?.length ? packageStates(state) : [state];
  return drafts.flatMap(draft => { const plan = deploymentSelection(draft); return Object.entries(plan.artifacts).flatMap(([id, plants]) => Object.keys(plants).map(plant => Object.values(plan.stages || { default: plan }).every(p => p.artifacts[id][plant]))); });
}
export function bulkManualEdgeChoices(state) {
  const drafts = state.selectedPackages?.length ? packageStates(state) : [state];
  return drafts.flatMap(draft => { const plan = deploymentSelection(draft); return Object.entries(plan.manualEdgeArtifacts).flatMap(([id, plants]) => Object.keys(plants).map(plant => Object.values(plan.stages || { default: plan }).every(p => p.manualEdgeArtifacts[id][plant]))); });
}
